import { useInfiniteQuery } from '@tanstack/react-query';
import { FlaskConical, Loader2, X } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { panel } from '@/components/surface/tones';
import { Pill, Skeleton } from '@/components/surface/ui';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { RUNS_KEY, automationApi } from '@/lib/automation-api';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { compactAgo, shortDateTime } from '@/modules/backups/format';
import { countsSentence, formatDuration } from '@/modules/automation/status';
import { PageHeader, RunStatusGlyph, RunStatusPill, Segmented } from '@/modules/automation/ui';
import type { RunKind, RunStatus } from '@/types/api';

const STATUS_OPTIONS: { value: RunStatus | ''; label: string }[] = [
  { value: '', label: 'Any status' },
  { value: 'awaiting_confirmation', label: 'Waiting for confirmation' },
  { value: 'running', label: 'Running' },
  { value: 'checking', label: 'Preflight' },
  { value: 'queued', label: 'Queued' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'partially_failed', label: 'Partially failed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

/**
 * The audit trail: every playbook, template and command that touched a server, who asked, and how it
 * ended on each machine. Newest first; a run is never edited after it ends — a retry is a new run
 * pointing back at the one it retries.
 */
export function RunsPage() {
  const [params, setParams] = useSearchParams();
  const kind = (params.get('kind') ?? '') as RunKind | '';
  const status = (params.get('status') ?? '') as RunStatus | '';
  const subject = params.get('subject') ?? '';
  const serverId = params.get('server_id') ?? '';

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    if (key === 'kind') next.delete('subject');
    setParams(next, { replace: true });
  };

  const runs = useInfiniteQuery({
    queryKey: [...RUNS_KEY, 'history', kind, status, subject, serverId],
    queryFn: ({ pageParam }) =>
      automationApi.listRuns({
        kind,
        status,
        ...(subject !== '' ? { subject } : {}),
        ...(serverId !== '' ? { serverId } : {}),
        ...(pageParam !== '' ? { cursor: pageParam } : {}),
        limit: 25,
      }),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: (query) => (query.state.data?.pages[0]?.items.some((run) => !run.is_finished) ? 3000 : false),
  });

  const items = runs.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Runs"
        description="Everything automation has done: every playbook, template and command, on which servers, who started it, what each server printed and how it ended."
      />

      <div className="flex flex-wrap items-center gap-3">
        <Segmented<RunKind | ''>
          ariaLabel="Kind of run"
          value={kind}
          onChange={(value) => update('kind', value)}
          options={[
            { value: '', label: 'All' },
            { value: 'playbook', label: 'Playbooks' },
            { value: 'template', label: 'Templates' },
            { value: 'command', label: 'Commands' },
          ]}
        />
        <div className="w-56">
          <Select aria-label="Status" value={status} onChange={(event) => update('status', event.target.value)}>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
        {subject !== '' && (
          <button type="button" onClick={() => update('subject', '')} className="inline-flex items-center gap-1 rounded-md bg-primary/[0.1] px-2 py-1 text-xs font-medium text-primary">
            {subject}
            <X className="size-3" aria-label="Clear" />
          </button>
        )}
        {serverId !== '' && (
          <button type="button" onClick={() => update('server_id', '')} className="inline-flex items-center gap-1 rounded-md bg-primary/[0.1] px-2 py-1 text-xs font-medium text-primary">
            One server
            <X className="size-3" aria-label="Clear" />
          </button>
        )}
      </div>

      {runs.isLoading && <Skeleton className="h-96" />}
      {runs.isError && <p className="text-sm text-danger">The runs could not be loaded: {getErrorMessage(runs.error)}</p>}

      {runs.isSuccess && items.length === 0 && (
        <div className={cn(panel, 'px-6 py-12 text-center')}>
          <p className="text-sm font-medium">No runs {kind !== '' || status !== '' || subject !== '' ? 'match these filters' : 'yet'}.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Start from <Link to="/automation/playbooks" className="text-primary hover:underline">a playbook</Link>,{' '}
            <Link to="/automation/templates" className="text-primary hover:underline">a template</Link> or{' '}
            <Link to="/automation/commands" className="text-primary hover:underline">a command</Link>.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <ul className={cn(panel, 'divide-y divide-slate-900/[0.06] overflow-hidden dark:divide-white/[0.05]')}>
          {items.map((run) => (
            <li key={run.id}>
              <Link to={`/automation/runs/${run.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5 transition-colors hover:bg-foreground/[0.03] sm:flex-nowrap">
                <RunStatusGlyph status={run.status} label={run.status_label} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium">{run.title}</span>
                    <Pill tone="muted">{run.kind_label}</Pill>
                    {run.mode === 'dry_run' && (
                      <Pill tone="primary" icon={FlaskConical}>
                        Dry run
                      </Pill>
                    )}
                  </div>
                  <p className="truncate text-xs text-muted-foreground">
                    {run.targets_label} · {countsSentence(run)} · {run.requested_by || 'unknown'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground">
                  <RunStatusPill status={run.status} label={run.status_label} />
                  <span className="hidden w-14 text-right tabular-nums md:inline">
                    {run.duration_seconds !== null && formatDuration(run.duration_seconds)}
                  </span>
                  <span className="w-16 text-right" title={shortDateTime(run.created_at)}>
                    {compactAgo(run.created_at)}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {runs.hasNextPage && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => void runs.fetchNextPage()} disabled={runs.isFetchingNextPage}>
            {runs.isFetchingNextPage && <Loader2 className="animate-spin" />}
            Load older runs
          </Button>
        </div>
      )}
    </div>
  );
}
